/**
 * Google IMA VideoAdClient — full AdsLoader / AdsManager wiring.
 * When disabled (default), delegates to null client — no SDK load, no ad requests.
 * Fail-open: any SDK/ad error returns content playback (no hard block).
 */

import { createNullVideoAdClient } from "./VideoAdClient";
import { VideoAdLifecycle } from "./VideoAdLifecycle";
import { loadImaSdk } from "./ImaSdkLoader";
import { resolveModernVideoAdsFlags } from "./modernVideoAdsFlags";

/**
 * @param {object} [options]
 * @param {object} [options.features]
 * @param {object} [options.avod]
 * @param {boolean} [options.forceLab]
 * @param {HTMLElement} [options.adContainer]
 * @param {HTMLVideoElement} [options.contentVideoElement]
 * @param {function} [options.onEvent]
 * @returns {object} VideoAdClient
 */
export function createImaVideoAdClient(options) {
  options = options || {};
  var flags = resolveModernVideoAdsFlags(options);

  if (!flags.enabled) {
    return createNullVideoAdClient({
      onEvent: options.onEvent,
    });
  }

  var nullFallback = createNullVideoAdClient({ onEvent: options.onEvent });
  var listeners = {};
  var destroyed = false;
  var googleIma = null;
  var adsLoader = null;
  var adsManager = null;
  var adDisplayContainer = null;
  var contentCompleteWired = false;

  function emit(name, payload) {
    var list = listeners[name] || [];
    for (var i = 0; i < list.length; i++) {
      try {
        list[i](payload || {});
      } catch (e) {
        /* ignore */
      }
    }
    if (typeof options.onEvent === "function") {
      options.onEvent(name, payload || {});
    }
  }

  function destroyManager() {
    if (adsManager) {
      try {
        adsManager.destroy();
      } catch (e) {
        /* ignore */
      }
      adsManager = null;
    }
    setAdContainerInteractive(false);
  }

  function setAdContainerInteractive(active) {
    var container = options.adContainer;
    if (!container || !container.style) return;
    container.style.pointerEvents = active ? "auto" : "none";
  }

  function wireAdsManagerEvents(manager, ima) {
    var AdEvent = ima.AdEvent;
    var AdErrorEvent = ima.AdErrorEvent;

    function on(type, handler) {
      try {
        manager.addEventListener(type, handler);
      } catch (e) {
        /* ignore */
      }
    }

    on(AdEvent.Type.LOADED, function () {
      emit(VideoAdLifecycle.AD_RESPONSE, { sdk: "ima3" });
    });
    on(AdEvent.Type.STARTED, function (e) {
      setAdContainerInteractive(true);
      emit(VideoAdLifecycle.AD_START, { ad: e && e.getAd ? e.getAd() : null });
      emit(VideoAdLifecycle.AD_BREAK_START, {});
    });
    on(AdEvent.Type.IMPRESSION, function () {
      emit(VideoAdLifecycle.AD_IMPRESSION, {});
    });
    on(AdEvent.Type.FIRST_QUARTILE, function () {
      emit(VideoAdLifecycle.AD_FIRST_QUARTILE, {});
    });
    on(AdEvent.Type.MIDPOINT, function () {
      emit(VideoAdLifecycle.AD_MIDPOINT, {});
    });
    on(AdEvent.Type.THIRD_QUARTILE, function () {
      emit(VideoAdLifecycle.AD_THIRD_QUARTILE, {});
    });
    on(AdEvent.Type.COMPLETE, function () {
      emit(VideoAdLifecycle.AD_COMPLETE, {});
    });
    on(AdEvent.Type.SKIPPED, function () {
      emit(VideoAdLifecycle.AD_SKIP, {});
    });
    on(AdEvent.Type.CLICK, function () {
      emit(VideoAdLifecycle.AD_CLICK, {});
      emit(VideoAdLifecycle.AD_CLICK_TRACKING, {});
    });
    on(AdEvent.Type.PAUSED, function () {});
    on(AdEvent.Type.RESUMED, function () {});
    on(AdEvent.Type.ALL_ADS_COMPLETED, function () {
      setAdContainerInteractive(false);
      emit(VideoAdLifecycle.AD_BREAK_END, {});
      emit(VideoAdLifecycle.AD_POD_END, {});
      destroyManager();
    });
    on(AdEvent.Type.CONTENT_PAUSE_REQUESTED, function () {
      setAdContainerInteractive(true);
      emit(VideoAdLifecycle.AD_BREAK_START, { contentPause: true });
      try {
        if (options.contentVideoElement) options.contentVideoElement.pause();
      } catch (e) {
        /* ignore */
      }
    });
    on(AdEvent.Type.CONTENT_RESUME_REQUESTED, function () {
      setAdContainerInteractive(false);
      emit(VideoAdLifecycle.AD_BREAK_END, { contentResume: true });
      try {
        if (options.contentVideoElement) {
          var p = options.contentVideoElement.play();
          if (p && p.catch) p.catch(function () {});
        }
      } catch (e) {
        /* ignore */
      }
    });
    on(AdErrorEvent.Type.AD_ERROR, function (errEvent) {
      var err = errEvent && errEvent.getError ? errEvent.getError() : null;
      emit(VideoAdLifecycle.AD_ERROR, {
        reason: err && err.getMessage ? err.getMessage() : "ima_ad_error",
        code: err && err.getErrorCode ? err.getErrorCode() : null,
      });
      emit(VideoAdLifecycle.AD_NO_FILL, { reason: "ima_ad_error" });
      destroyManager();
    });
  }

  return {
    name: "ima",
    flags: flags,
    init: function () {
      if (destroyed) {
        return Promise.resolve();
      }
      return loadImaSdk({
        allowed: true,
        url: flags.imaSdkUrl,
      }).then(function (result) {
        if (destroyed) return;
        if (!result.loaded || !result.google || !result.google.ima) {
          emit(VideoAdLifecycle.AD_ERROR, {
            reason: result.reason || "ima_unavailable",
          });
          return;
        }
        googleIma = result.google.ima;
        try {
          googleIma.settings.setVpaidMode(
            googleIma.ImaSdkSettings.VpaidMode.INSECURE
          );
        } catch (e) {
          /* optional */
        }

        var container =
          options.adContainer ||
          (typeof document !== "undefined"
            ? document.createElement("div")
            : null);
        if (container && !container.getAttribute("data-tpg-ima-container")) {
          container.setAttribute("data-tpg-ima-container", "1");
          if (!container.style.position) {
            container.style.position = "absolute";
            container.style.inset = "0";
            container.style.pointerEvents = "none";
          }
        }

        if (!container || !options.contentVideoElement) {
          emit(VideoAdLifecycle.AD_RESPONSE, {
            sdk: "ima3",
            warning: "missing_ad_container_or_video",
          });
          return;
        }

        adDisplayContainer = new googleIma.AdDisplayContainer(
          container,
          options.contentVideoElement
        );
        try {
          adDisplayContainer.initialize();
        } catch (e) {
          /* may require user gesture — continue */
        }

        adsLoader = new googleIma.AdsLoader(adDisplayContainer);
        adsLoader.addEventListener(
          googleIma.AdsManagerLoadedEvent.Type.ADS_MANAGER_LOADED,
          function (adsManagerLoadedEvent) {
            destroyManager();
            try {
              adsManager = adsManagerLoadedEvent.getAdsManager(
                options.contentVideoElement,
                new googleIma.AdsRenderingSettings()
              );
            } catch (e) {
              emit(VideoAdLifecycle.AD_ERROR, { reason: "ads_manager_create_failed" });
              emit(VideoAdLifecycle.AD_NO_FILL, { reason: "ads_manager_create_failed" });
              return;
            }
            wireAdsManagerEvents(adsManager, googleIma);
            try {
              var w =
                (options.adContainer && options.adContainer.clientWidth) ||
                (options.contentVideoElement &&
                  options.contentVideoElement.clientWidth) ||
                640;
              var h =
                (options.adContainer && options.adContainer.clientHeight) ||
                (options.contentVideoElement &&
                  options.contentVideoElement.clientHeight) ||
                360;
              adsManager.init(w, h, googleIma.ViewMode.NORMAL);
              adsManager.start();
              emit(VideoAdLifecycle.AD_POD_START, {});
            } catch (e) {
              emit(VideoAdLifecycle.AD_ERROR, {
                reason: e && e.message ? e.message : "ads_manager_start_failed",
              });
              emit(VideoAdLifecycle.AD_NO_FILL, { reason: "ads_manager_start_failed" });
              destroyManager();
            }
          },
          false
        );
        adsLoader.addEventListener(
          googleIma.AdErrorEvent.Type.AD_ERROR,
          function (adErrorEvent) {
            var err = adErrorEvent && adErrorEvent.getError
              ? adErrorEvent.getError()
              : null;
            emit(VideoAdLifecycle.AD_ERROR, {
              reason: err && err.getMessage ? err.getMessage() : "ima_loader_error",
            });
            emit(VideoAdLifecycle.AD_NO_FILL, { reason: "ima_loader_error" });
          },
          false
        );

        if (!contentCompleteWired && options.contentVideoElement) {
          contentCompleteWired = true;
          options.contentVideoElement.addEventListener("ended", function () {
            if (adsLoader) {
              try {
                adsLoader.contentComplete();
              } catch (e) {
                /* ignore */
              }
            }
          });
        }

        emit(VideoAdLifecycle.AD_RESPONSE, { sdk: "ima3", ready: true });
      });
    },
    requestAds: function (request) {
      request = request || {};
      emit(VideoAdLifecycle.AD_REQUEST, { request: request });
      if (!googleIma || !adsLoader) {
        emit(VideoAdLifecycle.AD_NO_FILL, {
          reason: "ima_not_initialized",
        });
        return Promise.resolve({ filled: false, reason: "ima_not_initialized" });
      }

      var adTagUrl =
        request.vmapTagUrl ||
        request.vmap_tag_url ||
        request.vastTagUrl ||
        request.vast_tag_url ||
        request.adTagUrl ||
        null;

      if (!adTagUrl && request.breaks && request.breaks.length) {
        var first = request.breaks[0] || {};
        adTagUrl =
          first.vmapTagUrl ||
          first.vmap_tag_url ||
          first.vastTagUrl ||
          first.vast_tag_url ||
          null;
      }

      if (!adTagUrl) {
        emit(VideoAdLifecycle.AD_NO_FILL, { reason: "no_ad_tag" });
        return Promise.resolve({ filled: false, reason: "no_ad_tag" });
      }

      try {
        if (adDisplayContainer) {
          try {
            adDisplayContainer.initialize();
          } catch (e) {
            /* gesture */
          }
        }
        var adsRequest = new googleIma.AdsRequest();
        adsRequest.adTagUrl = String(adTagUrl);
        adsRequest.linearAdSlotWidth =
          (options.adContainer && options.adContainer.clientWidth) || 640;
        adsRequest.linearAdSlotHeight =
          (options.adContainer && options.adContainer.clientHeight) || 360;
        adsRequest.nonLinearAdSlotWidth = adsRequest.linearAdSlotWidth;
        adsRequest.nonLinearAdSlotHeight = Math.min(
          150,
          adsRequest.linearAdSlotHeight
        );
        adsLoader.requestAds(adsRequest);
        return Promise.resolve({ filled: true, pending: true, adTagUrl: adTagUrl });
      } catch (e) {
        emit(VideoAdLifecycle.AD_ERROR, {
          reason: e && e.message ? e.message : "request_ads_failed",
        });
        emit(VideoAdLifecycle.AD_NO_FILL, { reason: "request_ads_failed" });
        return Promise.resolve({ filled: false, reason: "request_ads_failed" });
      }
    },
    destroy: function () {
      destroyed = true;
      destroyManager();
      if (adsLoader) {
        try {
          adsLoader.destroy();
        } catch (e) {
          /* ignore */
        }
        adsLoader = null;
      }
      adDisplayContainer = null;
      googleIma = null;
      listeners = {};
      nullFallback.destroy();
    },
    on: function (eventName, handler) {
      if (!listeners[eventName]) listeners[eventName] = [];
      listeners[eventName].push(handler);
      return function unsubscribe() {
        listeners[eventName] = (listeners[eventName] || []).filter(function (h) {
          return h !== handler;
        });
      };
    },
  };
}

export default createImaVideoAdClient;
